import {Record, List} from 'immutable';

import Address from 'web-app/react/entities/common/address';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import generateUUID from 'web-app/util/uuid';

import WarningModel from '../warnings/model';

export const Constants = {
    RELATION_TYPE: 'RELATION',
    COMPANY_TYPE: 'COMPANY',
};

interface Image {
    empty: boolean;
    small: string;
    large: string;
}

export class RelationBundle extends Record<{
    name?: string;
    status?: string;
    image?: Image;
    ids: List<string>;
}>({
    name: undefined,
    status: undefined,
    image: undefined,
    ids: List(),
}) {}

export class Share extends Record<{
    billPayerId: string;
    childId: string;
    share: number;
}>({
    billPayerId: '',
    childId: '',
    share: 0,
}) {}

class References extends Record<{
    ref1: string;
}>({
    ref1: '',
}) {}

export class SensitiveInfo extends Record<{
    ssn: string | null;
}>({
    ssn: null,
}) {}

export class BillPayer extends Record<{
    billPayerId: string;
    // Bill payers can be contacts in disguise, in which case they wont have a billPayerId. The search id is used as the value when searching bill payers
    searchId: string;
    note: string;
    billPayerType: string;
    name?: string;
    address: ReturnType<typeof Address.record>;
    email?: string;
    phone?: string;
    directDebit: boolean;
    accountName?: string;
    accountNo?: string;
    sortCode?: string;
    billingReference?: string;
    IBAN?: string;
    BIC?: string;
    deleted: boolean;
    dateOfSignature?: string;
    institutionId?: string;
    relationBundles: List<RelationBundle>;
    // Used as a temporary array of relation ids that the backend will convert to relationBundles
    shares: List<any>;
    references: any;
    relationsIds: List<string>;
    balance: number;
    paymentDue: any;
    voucherCount: any;
    behaviors: BehaviorsCollection;
    balanceBehaviors: BehaviorsCollection;
    fetchingRecurringPayments: boolean;
    recurringPaymentIds: List<any>;
    creditBehaviors: BehaviorsCollection;
    surchargeBehaviors: BehaviorsCollection;
    fetchingBalance: boolean;
    accountItemIds: List<any>;
    mandateId?: string;
    warnings: List<any>;
    description?: string;
    sensitive: SensitiveInfo | null;
    externalId: string | null;
    // A list of account item ids that have newly been created. Used to combat replica lag
    expectedIds: string[];
    // A list of account item ids that have newly been deleted. Used to combat replica lag
    unexpectedIds: string[];
}>({
    billPayerId: '',
    // Bill payers can be contacts in disguise, in which case they wont have a billPayerId. The search id is used as the value when searching bill payers
    searchId: '',
    note: '',
    billPayerType: '',
    name: undefined,
    address: Address.default,
    email: undefined,
    phone: undefined,
    directDebit: false,
    accountName: undefined,
    accountNo: undefined,
    sortCode: undefined,
    billingReference: undefined,
    IBAN: undefined,
    BIC: undefined,
    deleted: false,
    dateOfSignature: undefined,
    institutionId: undefined,
    relationBundles: List(),
    // Used as a temporary array of relation ids that the backend will convert to relationBundles
    shares: List(),
    references: undefined,
    relationsIds: List(),
    balance: 0,
    paymentDue: undefined,
    voucherCount: undefined,
    behaviors: new BehaviorsCollection([]),
    balanceBehaviors: new BehaviorsCollection([]),
    fetchingRecurringPayments: false,
    recurringPaymentIds: List(),
    creditBehaviors: new BehaviorsCollection([]),
    surchargeBehaviors: new BehaviorsCollection([]),
    fetchingBalance: false,
    accountItemIds: List(),
    mandateId: undefined,
    warnings: List(),
    description: undefined,
    sensitive: null,
    externalId: null,
    expectedIds: [],
    unexpectedIds: [],
}) {}

export const fromAPI = (data: any) => {
    return new BillPayer({
        ...data,
        searchId: data.billPayerId || generateUUID(),
        address: Address.fromAPI(data.address),
        references: new References(data.references),
        shares: List(data.shares).map((share: any) => new Share(share)),
        behaviors: new BehaviorsCollection(data.behaviors),
        relationBundles: List(data.relationBundles).map((bundle: any) => {
            return new RelationBundle(bundle);
        }),
        warnings: List(data.warnings).map((warning: any) => WarningModel.fromAPI(warning)),
    });
};

const toAPI = (billPayer: BillPayer) => billPayer.toJS();

export default {
    Constants,
    record: BillPayer,
    default: new BillPayer(),
    fromAPI,
    toAPI,
};
